import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import FormField from '../../core/components/FormField.tsx';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import { useTranslation } from 'react-i18next';
import Page from '../../core/components/Page';
import Layout from '../../core/components/Layout.tsx';
import { useMutation, useQuery } from '@apollo/client';
import { GET_COMPANY_DETAILS, UPDATE_MFA_CONFIG } from '../queries.ts';
import { useToaster } from '../../core/components/Toast.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';

const CUSTOMER_MFA_FORM_DEFAULT_VALUES = {
    isRequired: false,
};

const CustomerMfaPage = () => {
    const { t } = useTranslation('customer');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: CUSTOMER_MFA_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const { data } = useQuery(GET_COMPANY_DETAILS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {},
    });
    const mfaConfig = data?.viewer?.customer?.mfaConfig;

    useEffect(() => {
        if (mfaConfig) {
            form.reset({
                ...CUSTOMER_MFA_FORM_DEFAULT_VALUES,
                isRequired: mfaConfig?.isRequired,
            });
        }
    }, [mfaConfig]);

    const [updateMfaConfig] = useMutation(UPDATE_MFA_CONFIG);
    const handleUpdateMfaConfig = async (formData) => {
        try {
            await updateMfaConfig({ variables: { input: formData } });
            publishToast({ description: t('mfa.form.updateSuccess'), status: 'success' });
            form.reset(formData);
        } catch (e) {
            console.error(e);
            publishToast({ description: t('mfa.form.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('mfa.title')}</Page.HeaderTitle>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <SettingsFormCard onSubmit={form.handleSubmit(handleUpdateMfaConfig)}>
                        <Controller
                            name="isRequired"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('mfa.form.isRequired')}
                                    error={formErrors.isRequired}
                                    helpText={t('mfa.form.isRequiredHelpText')}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={form.formState.isSubmitting}
                            errorCount={countFieldErrors(formErrors)}
                        />
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default CustomerMfaPage;
